import {
  type TDealer,
  type TDealerApi,
  type TDealerBank,
  type TDealerBankDetailed,
  type TDealerBankDto,
  type TDealerBankForm,
  type TDealerBrand,
  type TDealerBrandDetailed,
  type TDealerBrandDto,
  type TDealerBrandForm,
  type TDealerDetailed,
  type TDealerDetailedApi,
  type TDealerDto,
  type TDealerForm,
  type TDealerLeasingCompany,
  type TDealerLeasingCompanyDto,
  type TDealerLeasingCompanyForm
} from '@/components/AdminPanel/DealersPoints/types/dealers.types';
import { convertActiveStatus } from '@/components/AdminPanel/Shared/helpers/convertActiveStatus';
import { EDealersTypesCode } from '@/components/Shared/constants/dicts-codes';
import { DictTypes } from '@/constants';
import { keysToCamelCase, keysToSnakeCase } from '@/services/helpers';
import { type KeysToSnakeCase } from '@/types/app';
import { type TDict } from '@/types/dicts';

export const dealersMapperFromApi = (dealer: TDealerApi): TDealer => {
  const camelCased = keysToCamelCase(dealer);
  return {
    ...camelCased,
    dealersBrandsList: sortDealerBrands(camelCased.dealersBrands)
      .map((brand) => brand.brand.name)
      .join(', '),
    isActive: convertActiveStatus.toStr(camelCased.isActive)
  };
};

export const dealersDetailedMapperFromApi = (
  dealer: TDealerDetailedApi
): TDealerDetailed => {
  const camelCased = keysToCamelCase(dealer);
  return {
    ...camelCased,
    dealersBanks: sortDealerBanks(camelCased.dealersBanks),
    dealersBrands: sortDealerBrands(camelCased.dealersBrands),
    isActive: convertActiveStatus.toStr(camelCased.isActive)
  };
};

export const dealersMapperToForm = (
  dealer?: TDealerDetailed | null
): TDealerForm => {
  if (!dealer)
    return {
      banks: [],
      bin: '',
      bitrixId: '',
      brands: [],
      cityName: '',
      code: '',
      dealerTypeUuid: '',
      details: '',
      ffbPoscredCode: '',
      forteId: '',
      fortePoscredCode: '',
      halykDealerId: '',
      hasCommercial: 'no',
      isActive: '',
      isSoldUsedCars: 'no',
      leasingCompanies: [],
      legalAddress: '',
      name: '',
      nameEurasian: '',
      partnerBin: '',
      partnerName: '',
      physicalAddress: '',
      publicCert: undefined,
      usedDealers: [],
      uuid: '',
      workpointName: ''
    };

  return {
    banks: dealer.dealersBanks.map(dealerBanksMapperToForm),
    bin: dealer.bin || '',
    bitrixId: dealer.bitrixId || '',
    brands: dealer.dealersBrands.map(dealerBrandsMapperToForm),
    cityName: dealer.city.name,
    code: String(dealer.code ?? ''),
    dealerTypeUuid: dealer.dealerType?.uuid || '',
    details: dealer.details || '',
    ffbPoscredCode: dealer.poscredDealers?.[0]?.ffinCode || '',
    forteId: dealer.forteId || '',
    fortePoscredCode: dealer.poscredDealers?.[0]?.forteCode || '',
    leasingCompanies: dealer.dealersLeasingCompanies.map(
      dealerLeasingCompaniesMapperToForm
    ),
    halykDealerId: dealer.halykDealerId || '',
    hasCommercial: dealer.hasCommercial ? 'yes' : 'no',
    isActive: dealer.isActive,
    isSoldUsedCars: dealer.isSoldUsedCars ? 'yes' : 'no',
    legalAddress: dealer.legalAddress || '',
    name: dealer.name,
    nameEurasian: dealer.nameEurasian || '',
    partnerBin: dealer.partnerBin || '',
    partnerName: dealer.partnerName || '',
    physicalAddress: dealer.physicalAddress || '',
    publicCert: dealer.publicCertLink
      ? {
          name: dealer.publicCertLink.split('/').pop() || '',
          url: dealer.publicCertLink
        }
      : undefined,
    usedDealers: (dealer.dudDealer ?? []).map((relation) => ({
      dealerId: relation.usedDealer.uuid,
      isDefaultDealer: relation.isDefaultDealer
    })),
    uuid: dealer.uuid,
    workpointName: dealer.workpointName || ''
  };
};

export const dealersMapperToDto = (
  dealer: TDealerForm,
  dicts: TDict | null
): TDealerDto => {
  const snakeCased = keysToSnakeCase(dealer);

  const cityUuid =
    dicts?.[DictTypes.Cities]?.getBy.name[snakeCased.city_name]?.uuid;

  const result: TDealerDto = {
    Dealers_Banks: snakeCased.banks.map(dealerBanksMapperToDto),
    bin: snakeCased.bin,
    bitrix_id: snakeCased.bitrix_id,
    brands: dealer.brands.map(dealerBrandsMapperToDto),
    city_uuid: cityUuid || '',
    code: Number(snakeCased.code),
    dealer_type_uuid: snakeCased.dealer_type_uuid,
    ffb_poscred_code: snakeCased.ffb_poscred_code,
    forte_id: snakeCased.forte_id,
    forte_poscred_code: snakeCased.forte_poscred_code,
    halyk_dealer_id: snakeCased.halyk_dealer_id,
    has_commercial: snakeCased.has_commercial === 'yes',
    is_active: convertActiveStatus.toBoolean(snakeCased.is_active),
    is_sold_used_cars: snakeCased.is_sold_used_cars === 'yes',
    leasing_companies: snakeCased.leasing_companies.map(
      dealerLeasingCompaniesMapperToDto
    ),
    legal_address: snakeCased.legal_address,
    name: snakeCased.name,
    name_eurasian: snakeCased.name_eurasian?.trim() || '',
    physical_address: snakeCased.physical_address,
    uuid: snakeCased.uuid,
    workpoint_name: snakeCased.workpoint_name
  };

  const isTrc =
    dicts?.[DictTypes.DealersTypes]?.getBy.uuid[dealer.dealerTypeUuid]?.code ===
    EDealersTypesCode.TRC;

  if (isTrc) {
    result.used_dealers = (dealer.usedDealers ?? []).map((usedDealer) => ({
      dealer_id: usedDealer.dealerId,
      is_default_dealer: dealer.brands.some(
        (brand) => brand.usedDealerId === usedDealer.dealerId
      )
    }));
  }
  if (snakeCased.details) {
    result.details = snakeCased.details;
  }
  if (snakeCased.public_cert?.base64) {
    result.public_certificate_file = snakeCased.public_cert.base64;
  }
  if (snakeCased.partner_bin) {
    result.partner_bin = snakeCased.partner_bin;
  }
  if (snakeCased.partner_name) {
    result.partner_name = snakeCased.partner_name;
  }
  return result;
};

const dealerBanksMapperToForm = (
  dealerBank: TDealerBankDetailed
): TDealerBankForm => {
  return {
    bankUuid: dealerBank.bank.uuid,
    customTimeout: Number(dealerBank.customTimeout),
    dealerCode: String(dealerBank.dealerCode ?? ''),
    fullname: dealerBank.bank.fullname,
    isActive: convertActiveStatus.toStr(dealerBank.isActive),
    isPriorityTimeout: dealerBank.isPriorityTimeout ? 'yes' : 'no'
  };
};

const dealerBanksMapperToDto = (
  dealerBank: KeysToSnakeCase<TDealerBankForm>
): TDealerBankDto => {
  return {
    bank_uuid: dealerBank.bank_uuid,
    custom_timeout: dealerBank.custom_timeout,
    dealer_code: dealerBank.dealer_code,
    is_active: convertActiveStatus.toBoolean(dealerBank.is_active),
    is_priority_timeout: dealerBank.is_priority_timeout === 'yes'
  };
};

const dealerLeasingCompaniesMapperToForm = (
  dealerLeasingCompany: TDealerLeasingCompany
): TDealerLeasingCompanyForm => {
  return {
    code: dealerLeasingCompany.code,
    isActive: convertActiveStatus.toStr(dealerLeasingCompany.isActive),
    leasingCompanyUuid: dealerLeasingCompany.leasingCompany.uuid
  };
};

const dealerLeasingCompaniesMapperToDto = (
  dealerLeasingCompany: KeysToSnakeCase<TDealerLeasingCompanyForm>
): TDealerLeasingCompanyDto => {
  return {
    code: dealerLeasingCompany.code,
    is_active: convertActiveStatus.toBoolean(dealerLeasingCompany.is_active),
    leasing_company_uuid: dealerLeasingCompany.leasing_company_uuid
  };
};

const dealerBrandsMapperToForm = (
  dealerBrand: TDealerBrandDetailed
): TDealerBrandForm => {
  return {
    brandId: dealerBrand.brand.uuid,
    isAvailableOnline: dealerBrand.isAvailableOnline,
    name: dealerBrand.brand.name
  };
};

const dealerBrandsMapperToDto = (
  dealerBrand: TDealerBrandForm
): TDealerBrandDto => {
  return {
    brand_id: dealerBrand.brandId,
    is_available_online: dealerBrand.isAvailableOnline,
    name: dealerBrand.name
  };
};

const sortDealerBanks = <T extends TDealerBank | TDealerBankDetailed>(
  dealerBanks: T[]
): T[] => {
  return dealerBanks.sort((a, b) => {
    return a.bank.name.localeCompare(b.bank.name);
  });
};

const sortDealerBrands = <T extends TDealerBrand | TDealerBrandDetailed>(
  dealerBrands: T[]
): T[] => {
  return dealerBrands.sort((a, b) => {
    return a.brand.name.localeCompare(b.brand.name);
  });
};
